import React, { useState, useEffect, useRef, useCallback } from "react";

import "./Mymemorial.css";
import { RiDeleteBinLine } from "react-icons/ri";
import dummy from "../../../assets/dummy.jpg";

import { useUserContext } from "../../../context/MemorialContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Button from "../../components/button/Button";
import { toast } from "react-toastify";
import { authFetch, getBackendUrl } from "../../../utils/memorialApi";
import { auth } from "../../../utils/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";

function Mymemorial() {
  const { user, handleChange } = useUserContext();
  const [memorials, setMemorials] = useState([]);
  const [incomingTransfers, setIncomingTransfers] = useState([]);
  const [outgoingTransfers, setOutgoingTransfers] = useState([]);
  const [transferActionId, setTransferActionId] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [memorialsLoading, setMemorialsLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const transfersSectionRef = useRef(null);
  const transferToastShown = useRef(false);
  const backendUrl = getBackendUrl();

  const fetchTransfers = useCallback(async () => {
    try {
      const res = await authFetch(`${backendUrl}/api/user/transfers`);
      if (res.ok) {
        const data = await res.json();
        setIncomingTransfers(data.incoming || []);
        setOutgoingTransfers(data.outgoing || []);
      }
    } catch (e) {
      console.error("Error fetching transfers:", e);
    }
  }, [backendUrl]);

  const fetchMemorials = useCallback(async () => {
    if (!auth.currentUser) {
      setMemorials([]);
      return;
    }
    setMemorialsLoading(true);
    try {
      const response = await authFetch(`${backendUrl}/api/user/memorials`);
      if (response.ok) {
        const data = await response.json();
        setMemorials(data.memorials || []);
      } else {
        console.error("Failed to fetch memorials", response.status);
        setMemorials([]);
      }
    } catch (error) {
      console.error("Error fetching memorials:", error);
      setMemorials([]);
    } finally {
      setMemorialsLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setAuthReady(true);
      if (firebaseUser) {
        await Promise.all([fetchMemorials(), fetchTransfers()]);
      } else {
        setMemorials([]);
        setIncomingTransfers([]);
        setOutgoingTransfers([]);
      }
    });
    return unsub;
  }, [fetchMemorials, fetchTransfers]);

  useEffect(() => {
    if (user?.meta?._id && auth.currentUser) {
      fetchMemorials();
      fetchTransfers();
    }
  }, [user?.meta?._id, fetchMemorials, fetchTransfers]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("purchaseType") === "FRAME_ONLY") {
      toast.success("Frame order received. We'll ship it to the address you provided at checkout.");
      navigate("/mymemo", { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const transferId = params.get("transfer");
    if (!transferId || !authReady) return;

    if (incomingTransfers.length > 0) {
      if (!transferToastShown.current) {
        toast.info("Review the memorial transfer invitation below.");
        transferToastShown.current = true;
      }
      transfersSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (location.search.includes("transfer=")) {
        navigate("/mymemo", { replace: true });
      }
    } else if (authReady && auth.currentUser) {
      fetchTransfers();
    }
  }, [incomingTransfers.length, authReady, location.search, navigate, fetchTransfers]);

  const handleDelete = async (id) => {
    try {
      const response = await authFetch(`${backendUrl}/api/memorials/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        toast.success("Memorial Deleted Successfully");
        fetchMemorials();
      } else {
        toast.error("Could not delete memorial");
      }
    } catch (error) {
      console.error("Error deleting memorial:", error);
    }
  };

  const handleTransferAction = async (transferId, action) => {
    setTransferActionId(transferId);
    try {
      const path =
        action === "accept"
          ? `${backendUrl}/api/transfers/${transferId}/accept`
          : `${backendUrl}/api/transfers/${transferId}/reject`;
      const res = await authFetch(path, { method: "PATCH" });
      const data = await res.json();
      if (res.ok) {
        toast.success(action === "accept" ? "Memorial transfer accepted" : "Transfer declined");
        fetchMemorials();
        fetchTransfers();
        navigate("/mymemo", { replace: true });
      } else {
        toast.error(data.error || "Transfer action failed");
      }
    } catch (e) {
      toast.error("Transfer action failed");
    } finally {
      setTransferActionId(null);
    }
  };

  const handleCancelTransfer = async (transferId) => {
    setTransferActionId(transferId);
    try {
      const res = await authFetch(`${backendUrl}/api/transfers/${transferId}`, {
        method: "DELETE",
      });
      if (res.ok) {
        toast.success("Transfer invitation cancelled");
        fetchTransfers();
      } else {
        toast.error("Could not cancel transfer");
      }
    } catch (e) {
      toast.error("Could not cancel transfer");
    } finally {
      setTransferActionId(null);
    }
  };

  return (
    <div className="memorialPage">
      <h2>My Memorials</h2>

      {authReady && !auth.currentUser && (
        <div className="memorialCard" style={{ marginBottom: 24, textAlign: "center" }}>
          <p style={{ color: "#5c5470", marginBottom: 16 }}>
            Sign in to view your memorials and transfer invitations.
          </p>
          <Link to="/login">
            <Button text="Login" />
          </Link>
        </div>
      )}

      {incomingTransfers.length > 0 && (
        <div
          ref={transfersSectionRef}
          className="memorialCard transfer-invitations-card"
          style={{ marginBottom: 24 }}
        >
          <h2>Memorial transfer invitations</h2>
          {incomingTransfers.map((transfer) => (
            <div key={transfer._id} className="secondLine">
              <div className="innerCard">
                <div className="left">
                  <div className="avatart">
                    <img
                      src={transfer.memorialId?.displayPicture || dummy}
                      alt=""
                    />
                  </div>
                  <div className="nameEmail">
                    <span>{transfer.memorialName || "Memorial"}</span>
                    <p className="transfer-invite-detail">
                      {transfer.fromEmail} invited you to manage this memorial
                    </p>
                  </div>
                </div>
                <div className="memoButtons" style={{ display: "flex", gap: 8 }}>
                  <Button
                    text="Accept"
                    isLoading={transferActionId === transfer._id}
                    onClick={() => handleTransferAction(transfer._id, "accept")}
                  />
                  <Button
                    text="Decline"
                    onClick={() => handleTransferAction(transfer._id, "reject")}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {outgoingTransfers.length > 0 && (
        <div className="memorialCard" style={{ marginBottom: 24 }}>
          <h2>Pending transfers you sent</h2>
          {outgoingTransfers.map((transfer) => (
            <div key={transfer._id} className="secondLine">
              <div className="innerCard">
                <div className="nameEmail">
                  <span>{transfer.memorialName}</span>
                  <p className="transfer-invite-detail">
                    Waiting for {transfer.toEmail}
                    {!transfer.toUserId && " to create an account"}
                  </p>
                  {transfer.expiresAt && (
                    <p className="transfer-invite-detail" style={{ fontSize: "0.85rem" }}>
                      {(() => {
                        const daysLeft = Math.max(
                          0,
                          Math.ceil(
                            (new Date(transfer.expiresAt).getTime() - Date.now()) /
                              (1000 * 60 * 60 * 24)
                          )
                        );
                        if (daysLeft === 0) return "Expires today";
                        if (daysLeft === 1) return "Expires in 1 day";
                        return `Expires in ${daysLeft} days`;
                      })()}
                    </p>
                  )}
                </div>
                <Button
                  text="Cancel invitation"
                  isLoading={transferActionId === transfer._id}
                  onClick={() => handleCancelTransfer(transfer._id)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="memorialCard">
        <h2>Create a memorial</h2>
        <div className="firstlineInputs">
          <input
            name="firstName"
            onChange={(e) => handleChange(e)}
            className="f_input"
            type="text"
            placeholder="First Name"
          />
          <input
            name="lastName"
            onChange={(e) => handleChange(e)}
            className="f_input"
            type="text"
            placeholder="Last Name"
          />
          <Link to="/creatememorial">
            <Button text="Create" />
          </Link>
        </div>
        <h2>MY memorials</h2>
        {memorialsLoading && (
          <p style={{ color: "#6b6578", padding: "8px 0" }}>Loading memorials…</p>
        )}
        {!memorialsLoading && auth.currentUser && memorials.length === 0 && (
          <p style={{ color: "#6b6578", padding: "8px 0" }}>
            You don&apos;t have any memorials yet. Create one above.
          </p>
        )}
        {memorials.map((memorial) => (
          <Link
            to={`/profile/${memorial.slug || memorial.firstName + memorial.lastName}/${memorial._id}`}
            key={memorial._id}
          >
            <div className="secondLine">
              <div className="innerCard">
                <div className="left">
                  <div className="avatart">
                    <img src={memorial.displayPicture || dummy} alt="" />
                  </div>
                  <div className="nameEmail">
                    <span>
                      {memorial.firstName} {memorial.lastName}
                    </span>
                    <p>{memorial.webAddress || memorial.slug}</p>
                  </div>
                </div>
                <div className="memoButtons">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleDelete(memorial._id);
                    }}
                    className="delBtn"
                    type="button"
                  >
                    <RiDeleteBinLine />
                    Delete Memorial
                  </button>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export default Mymemorial;
